import { withAuthRecovery } from '@/lib/authRecovery'
import { getSupabase } from '@/lib/supabase'
import type {
  ShmpCandidateRow,
  ShmpCourseNumber,
  ShmpCourseWeeklyPlanRow,
  ShmpDashboardData,
  ShmpListokFactSummary,
  ShmpMemberRow,
  ShmpMembershipHistoryRow,
  ShmpScheduledAdditionRow,
} from '@/types'

export interface ShmpWeekStart {
  week_id: number
  period_id: number
  week_number_in_period: number
  week_start: string
  week_end: string
}

export interface SaveShmpCourseWeeklyPlanItem {
  week_id: number
  course_number: ShmpCourseNumber
  planned_studio_count: number | null
  planned_revenue_per_studio_rub: number | null
}

function formatError(error: unknown): string {
  if (!error) return 'Unknown error'
  if (error instanceof Error) return error.message
  if (typeof error === 'object') {
    const value = error as { message?: string; details?: string; hint?: string; code?: string }
    return [value.message, value.details, value.hint, value.code].filter(Boolean).join(' | ')
  }
  return String(error)
}

export async function listShmpMembers(params?: {
  asOfDate?: string
  courseNumber?: ShmpCourseNumber | null
}): Promise<ShmpMemberRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('list_shmp_members', {
      p_as_of_date: params?.asOfDate ?? null,
      p_course_number: params?.courseNumber ?? null,
    })
  )
  if (error) throw new Error(formatError(error))
  return (data ?? []) as ShmpMemberRow[]
}

export async function listShmpCandidates(search?: string): Promise<ShmpCandidateRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('list_shmp_candidates', {
      p_search: search?.trim() || null,
      p_limit: 100,
      p_offset: 0,
    })
  )
  if (error) throw new Error(formatError(error))
  return (data ?? []) as ShmpCandidateRow[]
}

export async function getShmpMembershipHistory(
  partnerNumber: number
): Promise<ShmpMembershipHistoryRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('get_shmp_membership_history', {
      p_partner_number: partnerNumber,
    })
  )
  if (error) throw new Error(formatError(error))
  return (data ?? []) as ShmpMembershipHistoryRow[]
}

export async function listScheduledShmpAdditions(): Promise<ShmpScheduledAdditionRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('list_scheduled_shmp_additions')
  )
  if (error) throw new Error(formatError(error))
  return (data ?? []) as ShmpScheduledAdditionRow[]
}

export async function listShmpWeekStarts(): Promise<ShmpWeekStart[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase
      .from('metric_weeks')
      .select('week_id, period_id, week_number_in_period, week_start, week_end')
      .order('week_start', { ascending: true })
  )
  if (error) throw new Error(formatError(error))
  return (data ?? []) as ShmpWeekStart[]
}

export async function listShmpCourseWeeklyPlan(params: {
  periodId: number
  courseNumber?: ShmpCourseNumber | null
}): Promise<ShmpCourseWeeklyPlanRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('list_shmp_course_weekly_plan', {
      p_period_id: params.periodId,
      p_course_number: params.courseNumber ?? null,
    })
  )
  if (error) throw new Error(formatError(error))
  return (data ?? []) as ShmpCourseWeeklyPlanRow[]
}

export async function saveShmpCourseWeeklyPlan(params: {
  periodId: number
  items: SaveShmpCourseWeeklyPlanItem[]
}): Promise<ShmpCourseWeeklyPlanRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('save_shmp_course_weekly_plan', {
      p_period_id: params.periodId,
      p_items: params.items,
    })
  )
  if (error) throw new Error(formatError(error))
  return (data ?? []) as ShmpCourseWeeklyPlanRow[]
}

export async function getShmpListokFact(params: {
  periodId: number
  weekNumber?: number | null
  courseNumber?: ShmpCourseNumber | null
}): Promise<ShmpListokFactSummary> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('get_shmp_listok_fact', {
      p_period_id: params.periodId,
      p_week_number: params.weekNumber ?? null,
      p_course_number: params.courseNumber ?? null,
    })
  )
  if (error) throw new Error(formatError(error))
  return ((data ?? [])[0] ?? { fact_revenue_rub: 0 }) as ShmpListokFactSummary
}

export async function addShmpMember(params: {
  partnerNumber: number
  courseNumber: ShmpCourseNumber
  enrolledFrom: string
  reason?: string | null
}): Promise<number> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('add_shmp_member', {
      p_partner_number: params.partnerNumber,
      p_course_number: params.courseNumber,
      p_enrolled_from: params.enrolledFrom,
      p_reason: params.reason?.trim() || null,
    })
  )
  if (error) throw new Error(formatError(error))
  return data as number
}

export async function transferShmpMember(params: {
  partnerNumber: number
  newCourseNumber: ShmpCourseNumber
  effectiveFrom: string
  reason?: string | null
}): Promise<number> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('transfer_shmp_member', {
      p_partner_number: params.partnerNumber,
      p_new_course_number: params.newCourseNumber,
      p_effective_from: params.effectiveFrom,
      p_reason: params.reason?.trim() || null,
    })
  )
  if (error) throw new Error(formatError(error))
  return data as number
}

export async function excludeShmpMember(params: {
  membershipId: number
  effectiveTo: string
  reason?: string | null
}): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('exclude_shmp_member', {
      p_membership_id: params.membershipId,
      p_effective_to: params.effectiveTo,
      p_reason: params.reason?.trim() || null,
    })
  )
  if (error) throw new Error(formatError(error))
}

export async function cancelScheduledShmpChange(params: {
  membershipId: number
  reason?: string | null
}): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('cancel_scheduled_shmp_change', {
      p_membership_id: params.membershipId,
      p_reason: params.reason?.trim() || null,
    })
  )
  if (error) throw new Error(formatError(error))
}

export async function getShmpDashboard(params: {
  periodId: number
  weekNumber?: number | null
  courseNumber?: ShmpCourseNumber | null
}): Promise<ShmpDashboardData> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('get_shmp_dashboard', {
      p_period_id: params.periodId,
      p_week_number: params.weekNumber ?? null,
      p_course_number: params.courseNumber ?? null,
    })
  )
  if (error) throw new Error(formatError(error))
  return data as ShmpDashboardData
}
